import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    ColumnLayout,
    FormField,
    Header,
    Link,
    Popover,
    Select,
    SpaceBetween,
    StatusIndicator,
} from '@amzn/awsui-components-react';
import { CLI } from 'src/models/common/CLI';
import { ExecutionGroupDetails } from 'src/models/calculation-builder/ExecutonGroupDetails';
import { DataSourceRecord } from 'src/models/common/DataSourceRecord';
import { TPEBasicModal, ACTION_TYPE } from 'src/components/shared/TPEBasicModal';
import CONSTANTS from 'src/utils/constants';

interface ExecutionGroupSelectorProps {
    cliExecutionGroups: any[];
    executionGroupDetails?: ExecutionGroupDetails;
    canEditCLIAttributes: boolean;
    calculation?: CLI;
    saveExecutionGroup: (groupNumber: string) => any;
    isSaving: boolean;
    dataSources?: DataSourceRecord[];
}

export default function ExecutionGroupSelector(props: ExecutionGroupSelectorProps) {
    const {
        cliExecutionGroups,
        executionGroupDetails,
        canEditCLIAttributes,
        calculation,
        saveExecutionGroup,
        isSaving,
        dataSources,
    } = props;

    const options = [{ label: 'Select', value: '' }];
    cliExecutionGroups.forEach((x: string) => options.push({ label: `Group ${x}`, value: `${x}` }));

    const [isEditing, setIsEditing] = useState(false);
    const [selectedOption, setSelectedOption] = useState<any>(options[0]);
    const [showConfirmation, setShowConfirmation] = useState(false);

    useEffect(() => {
        resetDropdown();
    }, [executionGroupDetails]);

    const resetSelectedOption = () => {
        setSelectedOption(options.find((o) => o.value === executionGroupDetails?.executionGroup) || options[0]);
    };

    const resetDropdown = () => {
        resetSelectedOption();
        // Only set to edit mode if executionGroupDetails is loaded and group is explicitly not assigned
        // If executionGroupDetails is undefined (still loading), stay in view mode
        if (executionGroupDetails) {
            setIsEditing(executionGroupDetails.executionGroup == null);
        } else {
            setIsEditing(false);
        }
    };

    const selectGroup = (selectedGroup: any) => {
        setSelectedOption(selectedGroup);
        if (selectedGroup == options[0] || selectedGroup.value == executionGroupDetails?.executionGroup) {
            return;
        }
        setShowConfirmation(true);
    };

    const cancelChange = () => {
        setShowConfirmation(false);
        resetDropdown();
    };

    const saveChange = () => {
        setShowConfirmation(false);
        saveExecutionGroup(selectedOption.value);
    };

    const getMDAMessage = () => {
        if (!dataSources || dataSources.length === 0) return '';

        const mdaDataSources = dataSources.filter(
            (ds) =>
                ds.datasource === CONSTANTS.DATA_SOURCE_TYPES.MULTIDIMENSIONAL_ALLOCATION_WORKSHEET &&
                ds.multiAllocationWorksheetId
        );

        if (mdaDataSources.length === 0) return '';

        const worksheetNames = [...new Set(mdaDataSources.map((ds) => ds.multiAllocationWorksheetName))];
        const worksheetList = worksheetNames.join(', ');

        return ` Additionally, this change affects ${mdaDataSources.length} MDA data source line(s) associated with worksheet(s): ${worksheetList}. You must either update each data-source line to pull outputs from MDA worksheets in the new group, or update the MDA worksheets themselves to match the CLI's group.`;
    };

    const groupSelectorDropdown = (
        <Select
            data-class="basicDropdown"
            disabled={!canEditCLIAttributes || isSaving}
            onChange={({ detail }) => selectGroup(detail.selectedOption)}
            selectedOption={selectedOption}
            options={options}
            placeholder="Select a group"
        />
    );

    const groupWithWorkbookPopover =
        executionGroupDetails?.workbookId && executionGroupDetails?.executionGroup ? (
            <Popover
                data-class="basicPopover"
                dismissButton={false}
                position="top"
                content={`This execution group for workbook ${executionGroupDetails.workbookId} (${executionGroupDetails.workbookName}) was updated by user ${executionGroupDetails.lastUpdateUser} from calculation ${executionGroupDetails.updatedFromCalculation} on ${executionGroupDetails.lastUpdateDate}.`}>
                <Link variant="secondary">Group {executionGroupDetails.executionGroup}</Link>
            </Popover>
        ) : null;

    const groupPlainText =
        executionGroupDetails?.executionGroup && !executionGroupDetails?.workbookId ? (
            <span className="smallTextSpan">Group {executionGroupDetails.executionGroup}</span>
        ) : null;

    const noGroupAssignedText = !executionGroupDetails?.executionGroup ? (
        <span className="smallTextSpan" style={{ fontStyle: 'italic', color: '#687078' }}>
            No group assigned
        </span>
    ) : null;

    const groupDisplayInViewMode = (
        <Box display="inline-block">{groupWithWorkbookPopover || groupPlainText || noGroupAssignedText}</Box>
    );

    const loadingIndicator = isSaving ? <StatusIndicator type="loading">Saving...</StatusIndicator> : null;

    const editButton =
        !isSaving && canEditCLIAttributes && !isEditing ? (
            <Button
                data-class="smallIconButton"
                variant="icon"
                iconName="edit"
                onClick={() => {
                    setIsEditing(true);
                    resetSelectedOption();
                }}
                ariaLabel="Edit group assignment"
            />
        ) : null;

    // Determine which action control to show
    const actionControl = loadingIndicator || editButton;

    return (
        <>
            <Box>
                <SpaceBetween direction="horizontal" size="s" alignItems="center">
                    <Box display="inline-block" margin={{ right: 's' }}>
                        <strong>Assign CLI to a Group:</strong>
                    </Box>

                    {isEditing ? groupSelectorDropdown : groupDisplayInViewMode}

                    {actionControl}
                </SpaceBetween>
            </Box>
            <TPEBasicModal
                className="updateExecutionGroupConfirmationModal"
                visible={showConfirmation}
                action={ACTION_TYPE.CONFIRM_CANCEL}
                title="Assign execution group confirmation"
                onCancel={cancelChange}
                onConfirm={saveChange}>
                <div>
                    {calculation?.workbookId == null
                        ? `This will assign this calculation to ${selectedOption.label}.${getMDAMessage()} Confirm to proceed.`
                        : `This will assign all calculations in workbook ${calculation?.workbookId} - ${calculation?.workbookName} to ${selectedOption.label}.${getMDAMessage()} Confirm to proceed.`}
                </div>
            </TPEBasicModal>
        </>
    );
}
